import {
  AddAliasRequest,
  ConsolidateOfficeLocationsRequest,
  OfficeLocation,
  OfficeLocationAlias,
  OfficeLocationCreateRequest,
  OfficeLocationDictionaryModel,
  OfficeLocationUpdateRequest,
} from "../../models/officeLocation.model";
import { BackendHttpService } from "../backendHttpService";

export class OfficeLocationAdminService extends BackendHttpService {
  private officeLocationsDictionaryCache:
    | OfficeLocationDictionaryModel[]
    | null = null;

  constructor() {
    super("dictionary/officelocations");
  }

  // Clear cache when dictionary changes
  private clearCache(): void {
    this.officeLocationsDictionaryCache = null;
  }

  // Dictionary methods for admin
  async getOfficeLocationsDictionary(): Promise<
    OfficeLocationDictionaryModel[]
  > {
    if (this.officeLocationsDictionaryCache) {
      return this.officeLocationsDictionaryCache;
    }
    try {
      this.officeLocationsDictionaryCache = await this.get<
        OfficeLocationDictionaryModel[]
      >("");
      return this.officeLocationsDictionaryCache;
    } catch (error) {
      console.error("Error getting office locations dictionary:", error);
      throw error;
    }
  }

  // CRUD operations (admin only)
  async createOfficeLocation(
    request: OfficeLocationCreateRequest
  ): Promise<OfficeLocation> {
    try {
      const officeLocation = await this.post<
        OfficeLocationCreateRequest,
        OfficeLocation
      >(request, "");
      this.clearCache();
      return officeLocation;
    } catch (error) {
      console.error("Error creating office location:", error);
      throw error;
    }
  }

  async updateOfficeLocation(
    officeLocationId: number,
    request: OfficeLocationUpdateRequest
  ): Promise<OfficeLocation> {
    try {
      const officeLocation = await this.put<
        OfficeLocationUpdateRequest,
        OfficeLocation
      >(request, `/${officeLocationId}`);
      this.clearCache();
      return officeLocation;
    } catch (error) {
      console.error("Error updating office location:", error);
      throw error;
    }
  }

  async deleteOfficeLocation(officeLocationId: number): Promise<void> {
    try {
      await this.delete(`/${officeLocationId}`);
      this.clearCache();
    } catch (error) {
      console.error("Error deleting office location:", error);
      throw error;
    }
  }

  async consolidateOfficeLocations(
    request: ConsolidateOfficeLocationsRequest
  ): Promise<OfficeLocation> {
    try {
      const officeLocation = await this.post<
        ConsolidateOfficeLocationsRequest,
        OfficeLocation
      >(request, "/consolidate");
      this.clearCache();
      return officeLocation;
    } catch (error) {
      console.error("Error consolidating office locations:", error);
      throw error;
    }
  }

  // Search for office locations by name with diacritics support (admin)
  async searchOfficeLocationsByName(
    name: string
  ): Promise<OfficeLocationDictionaryModel[]> {
    try {
      // Don't use cache for search to ensure real-time results
      const results = await this.get<OfficeLocationDictionaryModel[]>(
        `/search?name=${encodeURIComponent(name)}`
      );
      return results;
    } catch (error) {
      console.error("Error searching office locations by name:", error);
      throw error;
    }
  }

  // Admin review methods
  async getLocationsRequiringReview(): Promise<
    OfficeLocationDictionaryModel[]
  > {
    try {
      const results = await this.get<OfficeLocationDictionaryModel[]>(
        "/requiring-review"
      );
      return results;
    } catch (error) {
      console.error("Error getting office locations requiring review:", error);
      throw error;
    }
  }

  async markAsReviewed(officeLocationId: number): Promise<boolean> {
    try {
      const result = await this.put<void, boolean>(
        undefined,
        `/${officeLocationId}/mark-reviewed`
      );
      this.clearCache(); // Clear cache since location status changed
      return result;
    } catch (error) {
      console.error("Error marking office location as reviewed:", error);
      throw error;
    }
  }

  // Alias management methods (admin)
  async getAliases(officeLocationId: number): Promise<OfficeLocationAlias[]> {
    try {
      const aliases = await this.get<OfficeLocationAlias[]>(
        `/${officeLocationId}/aliases`
      );
      return aliases;
    } catch (error) {
      console.error("Error getting office location aliases:", error);
      throw error;
    }
  }

  async addAlias(request: AddAliasRequest): Promise<OfficeLocationAlias> {
    try {
      const alias = await this.post<AddAliasRequest, OfficeLocationAlias>(
        request,
        `/${request.officeLocationId}/aliases`
      );
      this.clearCache(); // Clear cache since aliases affect searching
      return alias;
    } catch (error) {
      console.error("Error adding office location alias:", error);
      throw error;
    }
  }

  async removeAlias(aliasId: number): Promise<boolean> {
    try {
      await this.delete(`/aliases/${aliasId}`);
      this.clearCache(); // Clear cache since aliases affect searching
      return true;
    } catch (error) {
      console.error("Error removing office location alias:", error);
      throw error;
    }
  }

  // Helper method to get office location by ID from cache or API (admin)
  async getOfficeLocationById(id: number): Promise<OfficeLocation | undefined> {
    try {
      const officeLocations = await this.getOfficeLocationsDictionary();
      return officeLocations.find((loc) => loc.id === id);
    } catch (error) {
      console.error("Error getting office location by ID:", error);
      throw error;
    }
  }
}

// Export singleton instance for convenience
export const officeLocationAdminService = new OfficeLocationAdminService();
